import { useMutation, useQuery } from "@apollo/client";
import AttachFile from "@mui/icons-material/AttachFile";
import { Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { GenericCommentStream } from "components/Comments/GenericCommentStream";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
  EditMode,
  FormState,
} from "components/Document/AddEditDocumentDialog";
import { DocumentType } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  ADD_HARMONIZED_VIOLATION_COMMENT,
  DELETE_HARMONIZED_VIOLATION_COMMENT,
  HARMONIZED_VIOLATION_COMMENTS,
  HARMONIZED_VIOLATION_DOCUMENTS,
} from "hooks/useHarmonizedViolations";
import { useMemo, useState } from "react";

import { SectionHeading } from "./violationDisplay";
import {
  attachmentLabel,
  ViolationAttachment,
  ViolationAttachmentChips,
} from "./ViolationDocumentsSection";

/** A document created through the composer's attach flow but not yet
 * submitted with a comment. */
type PendingAttachment = { id: string; title?: string | null; fileExtension?: string | null };

/**
 * The violation's comment thread — one shared stream for tenant users and staff.
 * Replaces the Airtable tracker's Communication Log and Notes for Next Steps
 * columns.
 */
export function ViolationCommentsSection({
  tenantId,
  violationId,
  facilityId,
}: {
  tenantId: string;
  violationId: string;
  /** Seeded onto the attach dialog so the resulting Document gets a
   * FacilityDocument row — see the identical note in ViolationDocumentsSection. */
  facilityId: string;
}) {
  const alerts = useAlerts();
  const { user } = useCurrentUser();

  const [addEditDialogState, setAddEditDialogState] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);

  const { data, loading } = useQuery(HARMONIZED_VIOLATION_COMMENTS, {
    variables: { tenantId, harmonizedViolationId: violationId },
  });

  const { data: docData } = useQuery(HARMONIZED_VIOLATION_DOCUMENTS, {
    variables: { tenantId, harmonizedViolationId: violationId },
  });

  const attachmentsByComment = useMemo(() => {
    const map = new Map<string, ViolationAttachment[]>();
    for (const row of docData?.harmonizedViolationDocuments ?? []) {
      if (!row.addedByCommentId) continue;
      const existing = map.get(row.addedByCommentId) ?? [];
      existing.push(row);
      map.set(row.addedByCommentId, existing);
    }
    return map;
  }, [docData]);

  const [addComment, { loading: addLoading }] = useMutation(ADD_HARMONIZED_VIOLATION_COMMENT, {
    // The thread is drawer-local and absent from the grid query, so a refetch
    // here cannot disturb grid normalization. A comment with attachments changes
    // both the comments and documents queries.
    refetchQueries: [HARMONIZED_VIOLATION_COMMENTS, HARMONIZED_VIOLATION_DOCUMENTS],
    awaitRefetchQueries: true,
  });

  const [deleteComment, { loading: deleteLoading }] = useMutation(
    DELETE_HARMONIZED_VIOLATION_COMMENT,
    {
      refetchQueries: [HARMONIZED_VIOLATION_COMMENTS],
      awaitRefetchQueries: true,
    }
  );

  const onAddComment = async (message: string) => {
    try {
      await addComment({
        variables: {
          tenantId,
          harmonizedViolationId: violationId,
          comment: message,
          documentIds: pendingAttachments.map((doc) => doc.id),
        },
      });
      setPendingAttachments([]);
    } catch (e) {
      alerts.error("Could not add comment", e);
      // Rethrow so GenericCommentStream keeps the user's text in the input
      // rather than clearing it on a failed post.
      throw e;
    }
  };

  const onDeleteComment = async (commentId: string) => {
    try {
      await deleteComment({ variables: { tenantId, id: commentId } });
    } catch (e) {
      alerts.error("Could not delete comment", e);
      throw e;
    }
  };

  return (
    <Stack spacing={1} sx={{ mb: 3 }}>
      <SectionHeading>Comments</SectionHeading>
      <GenericCommentStream
        title=""
        comments={data?.harmonizedViolationComments ?? []}
        currentUserId={user?.id}
        onAddComment={onAddComment}
        onDeleteComment={onDeleteComment}
        addLoading={addLoading}
        deleteLoading={deleteLoading}
        showDeleteButton
        disabled={loading}
        emptyStateMessage="No comments yet."
        placeholder="Add a comment…"
        renderCommentFooter={(c) => (
          <ViolationAttachmentChips rows={attachmentsByComment.get(c.id) ?? []} />
        )}
        footerEnd={
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
              {pendingAttachments.map((doc) => (
                <Chip
                  key={doc.id}
                  size="small"
                  variant="outlined"
                  label={attachmentLabel({ document: doc })}
                  onDelete={() =>
                    setPendingAttachments((docs) => docs.filter((d) => d.id !== doc.id))
                  }
                />
              ))}
              <Tooltip title="Attach a document">
                <IconButton
                  size="small"
                  aria-label="Attach a document"
                  disabled={loading}
                  onClick={() => {
                    setAddEditDialogState({
                      ...defaultAddEditDocumentDialogState,
                      open: true,
                      mode: EditMode.ADD,
                      step: FormState.UPLOAD,
                      facilities: [{ id: facilityId }],
                    });
                  }}
                >
                  <AttachFile fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            {pendingAttachments.length > 0 ? (
              <Typography variant="caption" color="text.secondary">
                Attached when the comment is posted.
              </Typography>
            ) : null}
          </Stack>
        }
      />

      {addEditDialogState.open && (
        <AddEditDocumentDialog
          tenantId={tenantId}
          dialogState={addEditDialogState}
          setDialogState={setAddEditDialogState}
          documentTypeOptions={[DocumentType.Other]}
          showFacilityPicker={false}
          onSubmit={(document) => {
            // The Document row (and its FacilityDocument) already exists by
            // this point — AddEditDocumentDialog's upload step already ran
            // upsertDocument. Only the violation *attachment* is staged
            // locally here; it becomes a HarmonizedViolationDocument row
            // atomically when the comment carrying it is submitted, via
            // `documentIds` on the add-comment mutation.
            setPendingAttachments((docs) => [...docs, document]);
            setAddEditDialogState(defaultAddEditDocumentDialogState);
          }}
          refetchQueries={[]}
        />
      )}
    </Stack>
  );
}
